import { FC, ReactNode } from 'react'

import styled, { css } from 'styled-components'

import CheckMarkIcon from 'src/Assets/Svg/pages-icons/CheckMark.svg?react'
import CrossIcon from 'src/Assets/Svg/common/fullCross.svg?react'

export interface LabelForGenreOrLanguageProps {
  title: ReactNode
  onClick: () => void
  count?: number
  selected?: boolean
  disabled?: boolean
  hasCross?: boolean
  markedSelected?: boolean
}

const Container = styled.button<{
  $selected?: boolean
  $hasCross?: boolean
  $markedSelected?: boolean
  disabled?: boolean
}>`
  display: flex;
  align-items: center;
  padding: 8px 12px 9px 12px;
  gap: 8px;
  width: fit-content;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  border: 1px solid;
  transition: all 0.2s ease;

  ${({ theme, $selected, $hasCross, $markedSelected, disabled }) => {
    if (disabled) {
      return css`
        background-color: ${theme.color.lightGrey};
        border-color: ${theme.color.lightGrey};
        color: ${theme.color.white};
        svg {
          path {
            fill: ${theme.color.white} !important;
          }
        }
      `
    }
    if ($selected || $hasCross) {
      return css`
        background-color: ${theme.color.lightGreen};
        border-color: ${theme.color.darkGreen};
        color: ${theme.color.darkGreen};

        svg {
          path {
            fill: ${theme.color.darkGreen} !important;
          }
        }

        &:hover {
          background-color: ${theme.color.lightGrey};
          border-color: ${theme.color.lightGrey};
          color: ${theme.color.darkGreen};
          svg {
            path {
              fill: ${theme.color.darkGreen} !important;
            }
          }
        }

        &:focus-visible {
          background-color: ${theme.color.lightGrey};
          border-color: ${theme.color.darkGreen};
          color: ${theme.color.darkGreen};
          outline: none;
        }

        &:active {
          background-color: ${theme.color.lightGrey};
          border-color: ${theme.color.slateGrey};
          color: ${theme.color.slateGrey};
          svg {
            path {
              fill: ${theme.color.slateGrey} !important;
            }
          }
        }
      `
    }
    if ($markedSelected) {
      return css`
        background-color: ${theme.color.hoverWhite};
        border-color: ${theme.color.darkGrey};
        color: ${theme.color.darkGrey};

        svg {
          path {
            fill: ${theme.color.darkGrey} !important;
          }
        }

        &:hover {
          background-color: ${theme.color.lightGrey};
          border-color: ${theme.color.lightGrey};
          color: ${theme.color.darkGrey};
        }

        &:focus-visible {
          background-color: ${theme.color.lightGrey};
          border-color: ${theme.color.darkGreen};
          color: ${theme.color.darkGrey};
          outline: none;
        }

        &:active {
          background-color: ${theme.color.lightGrey};
          border-color: ${theme.color.slateGrey};
          color: ${theme.color.slateGrey};
          svg {
            path {
              fill: ${theme.color.slateGrey} !important;
            }
          }
        }
      `
    }
    return css`
      background-color: ${theme.color.hoverWhite};
      border-color: ${theme.color.hoverWhite};
      color: ${theme.color.darkGrey};

      &:hover {
        background-color: ${theme.color.lightGrey};
        border-color: ${theme.color.lightGrey};
        color: ${theme.color.darkGrey};
      }

      &:focus-visible {
        background-color: ${theme.color.lightGrey};
        border-color: ${theme.color.darkGreen};
        color: ${theme.color.darkGrey};
        outline: none;
      }

      &:active {
        background-color: ${theme.color.lightGrey};
        border-color: ${theme.color.slateGrey};
        color: ${theme.color.slateGrey};
      }
    `
  }}
`

const Text = styled.span`
  display: flex;
  text-align: center;
  gap: 4px;
`

export const LabelForGenreOrLanguage: FC<LabelForGenreOrLanguageProps> = ({
  title,
  onClick,
  count,
  selected,
  disabled,
  hasCross,
  markedSelected,
}) => (
  <Container
    $selected={selected}
    $hasCross={hasCross}
    $markedSelected={markedSelected}
    disabled={disabled}
    onClick={onClick}
    translate='no'
  >
    <Text>
      {title} {count ? ` (${count})` : ''}
    </Text>
    {(selected || hasCross) && <CrossIcon role='cross-icon' />}
    {markedSelected && !selected && !hasCross && (
      <CheckMarkIcon role='check-icon' />
    )}
  </Container>
)
